import { useState, useEffect } from "react";
import { TrendingDown, TrendingUp, AlertCircle, AlertTriangle, DollarSign, Clock, Flame, Wallet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../App";

const fmtCurr = (v) => v == null ? "—" : "$" + v.toLocaleString();

const HEALTH_STYLES = {
  healthy: { badge: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500" },
  caution: { badge: "bg-amber-100 text-amber-700", bar: "bg-amber-500" },
  near_breakeven: { badge: "bg-orange-100 text-orange-700", bar: "bg-orange-500" },
  loss: { badge: "bg-red-100 text-red-700", bar: "bg-red-500" }
};

function ProgramCostingCard({ program, onClick }) {
  const style = HEALTH_STYLES[program.margin_health] || HEALTH_STYLES.healthy;
  const costPercent = program.contract_value > 0 
    ? Math.min((program.total_cost / program.contract_value) * 100, 100) 
    : 0;

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors"
      data-testid={`costing-program-${program.program_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{program.program_name}</p>
          <p className="text-xs text-muted-foreground truncate">{program.client_name}</p>
        </div>
        <Badge className={`shrink-0 ${style.badge}`}>
          {program.gross_margin_percent?.toFixed(1) || 0}%
        </Badge>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Contract: {fmtCurr(program.contract_value)}</span>
          <span>Cost: {fmtCurr(program.total_cost)}</span>
        </div>
        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
          <div className={`absolute left-0 top-0 h-full ${style.bar}`} style={{ width: `${costPercent}%` }} />
        </div>
        {program.runway_to_target !== null && program.runway_to_target < 30 && (
          <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
            <Clock className="w-3 h-3" />
            <span>Runway: {program.runway_to_target}d to target</span>
          </div>
        )}
      </div>
      {program.has_missing_rates && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-600">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Missing resource rates</span>
        </div>
      )}
    </button>
  );
}

export default function MarginEngine({ data, loading }) {
  const nav = useNavigate();
  const [costingData, setCostingData] = useState(null);
  const [costingLoading, setCostingLoading] = useState(true);

  useEffect(() => {
    fetchCostingData();
  }, []);

  const fetchCostingData = async () => {
    try {
      const res = await apiClient.get("/costing/margin-protection-summary");
      setCostingData(res.data);
    } catch (err) {
      console.error("Failed to load costing data");
    }
    setCostingLoading(false);
  };

  if (loading || costingLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader><CardTitle className="h-6 bg-muted rounded w-48" /></CardHeader>
        <CardContent className="h-80" />
      </Card>
    );
  }

  // Use costing data if available, fall back to legacy data
  const useCostingData = costingData && costingData.summary;

  if (useCostingData) {
    const { summary, programs_at_risk, programs_near_breakeven, programs_in_loss, runway_warnings, missing_rates, unpaid_invoices } = costingData;
    const atRiskList = [...programs_in_loss, ...programs_near_breakeven, ...programs_at_risk];

    return (
      <Card data-testid="margin-engine-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'var(--font-sans)' }}>
              <TrendingDown className="w-5 h-5" />
              Margin Protection
            </CardTitle>
            {summary.total_at_risk > 0 && (
              <Badge variant="destructive">{summary.total_at_risk} at risk</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="text-center p-2 bg-red-50 rounded-lg">
              <p className="text-[10px] text-red-600 uppercase tracking-wider">In Loss</p>
              <p className="text-lg font-bold text-red-600">{summary.total_in_loss}</p>
            </div>
            <div className="text-center p-2 bg-orange-50 rounded-lg">
              <p className="text-[10px] text-orange-600 uppercase tracking-wider">Near B/E</p>
              <p className="text-lg font-bold text-orange-600">{summary.total_near_breakeven}</p>
            </div>
            <div className="text-center p-2 bg-amber-50 rounded-lg">
              <p className="text-[10px] text-amber-600 uppercase tracking-wider">Caution</p>
              <p className="text-lg font-bold text-amber-600">{summary.total_caution}</p>
            </div>
            <div className="text-center p-2 bg-blue-50 rounded-lg">
              <p className="text-[10px] text-blue-600 uppercase tracking-wider">Runway Warn</p>
              <p className="text-lg font-bold text-blue-600">{summary.programs_with_runway_warning}</p>
            </div>
          </div>

          {/* Quick Alerts */}
          {(summary.programs_missing_rates > 0 || summary.programs_with_unpaid > 0) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {summary.programs_missing_rates > 0 && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {summary.programs_missing_rates} missing rates
                </Badge>
              )}
              {summary.programs_with_unpaid > 0 && (
                <Badge variant="outline" className="text-red-600 border-red-300">
                  <Wallet className="w-3 h-3 mr-1" />
                  {summary.programs_with_unpaid} unpaid invoices
                </Badge>
              )}
            </div>
          )}

          {/* At Risk Programs */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Programs At Risk</p>
              <button 
                onClick={() => nav("/studio/project-costing")}
                className="text-xs text-primary hover:underline"
              >
                View All →
              </button>
            </div>
            
            {atRiskList.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-30 text-emerald-500" />
                <p className="text-emerald-600 font-medium">All programs healthy!</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                {atRiskList.slice(0, 8).map((prog) => (
                  <ProgramCostingCard
                    key={prog.program_id}
                    program={prog}
                    onClick={() => nav(`/programs/${prog.program_id}?tab=costing`)}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback to legacy data
  if (!data) return null;

  const { summary, programs, threshold_pct } = data;
  const atRiskPrograms = programs.filter(p => p.cost_pct > threshold_pct);

  return (
    <Card data-testid="margin-engine-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'var(--font-sans)' }}>
            <TrendingDown className="w-5 h-5" />
            Margin Protection
          </CardTitle>
          {atRiskPrograms.length > 0 && (
            <Badge variant="destructive">{atRiskPrograms.length} at risk</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Contract</p>
            <p className="text-lg font-semibold mt-1">{fmtCurr(summary.total_contract_value)}</p>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Allocated Cost</p>
            <p className="text-lg font-semibold mt-1">{fmtCurr(summary.total_allocated_cost)}</p>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Blended Margin</p>
            <p className={`text-lg font-semibold mt-1 ${summary.overall_margin_pct < 40 ? "text-red-500" : summary.overall_margin_pct < 60 ? "text-amber-500" : "text-green-500"}`}>
              {summary.overall_margin_pct}%
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Programs by Margin</p>
            <p className="text-xs text-muted-foreground">Threshold: {threshold_pct}%</p>
          </div>
          
          {programs.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No active programs with allocations</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {programs.slice(0, 10).map((prog) => (
                <button
                  key={prog.program_id}
                  onClick={() => nav(`/programs/${prog.program_id}`)}
                  className="w-full text-left p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{prog.program_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{prog.client_name}</p>
                    </div>
                    <Badge variant={prog.margin_status === "critical" ? "destructive" : prog.margin_status === "warning" ? "secondary" : "outline"} className="shrink-0">
                      {prog.margin_pct}%
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Contract: {fmtCurr(prog.contract_value)}</span>
                      <span>Cost: {fmtCurr(prog.allocated_cost)}</span>
                    </div>
                    <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`absolute left-0 top-0 h-full ${
                          prog.margin_status === "critical" ? "bg-red-500" : 
                          prog.margin_status === "warning" ? "bg-amber-500" : "bg-green-500"
                        }`}
                        style={{ width: `${Math.min(100 - prog.margin_pct, 100)}%` }}
                      />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
